import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { ApiError } from '../../../../lib/http/ApiError';
import { formatDateTime } from '../../../../lib/date/formatDateTime';
import type { GenFilesApi } from '../../api/gen-files.api';
import type { GenFileUploadResult } from '../../types/gen-file.types';
import theme from '../../../../theme/theme';

// HOMCO-275 spec: browser-side guard mirrors the backend 40 MB limit
const MAX_SIZE_BYTES = 40 * 1024 * 1024;

const KNOWN_REASONS = new Set([
  'no_file',
  'invalid_extension',
  'file_too_large',
  'unreadable_archive',
  'archive_too_large',
  'invalid_version',
  'unsupported_hc_version',
  'version_not_greater',
  'upload_in_progress',
  'missing_files',
]);

interface UploadGenModalProps {
  open: boolean;
  onClose: () => void;
  api: GenFilesApi;
  currentVersion: string | null;
  onSuccess: () => void;
}

type ModalStep = 'select' | 'sending' | 'result';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function validateFile(file: File | null, t: (key: string) => string): string | null {
  if (!file) return t('upload.validation.noFile');
  if (!file.name.toLowerCase().endsWith('.7z')) return t('upload.validation.invalidExtension');
  if (file.size > MAX_SIZE_BYTES) return t('upload.validation.tooLarge');
  return null;
}

export function UploadGenModal({ open, onClose, api, currentVersion, onSuccess }: UploadGenModalProps) {
  const { t } = useTranslation('generalSettings');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [missingFiles, setMissingFiles] = useState<string[]>([]);
  const [step, setStep] = useState<ModalStep>('select');
  const [result, setResult] = useState<GenFileUploadResult | null>(null);
  const sendingRef = useRef(false);

  const reset = useCallback(() => {
    setFile(null);
    setConfirmed(false);
    setValidationError(null);
    setBackendError(null);
    setMissingFiles([]);
    setStep('select');
    setResult(null);
    sendingRef.current = false;
  }, []);

  const handleClose = useCallback(() => {
    if (step === 'sending') return;
    if (step === 'result') onSuccess();
    reset();
    onClose();
  }, [step, onClose, onSuccess, reset]);

  const handleFileChange = useCallback(
    (selected: File | null) => {
      setFile(selected);
      setValidationError(null);
      setBackendError(null);
      setMissingFiles([]);
      if (selected) {
        const error = validateFile(selected, t);
        if (error) setValidationError(error);
      }
    },
    [t],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const dropped = event.dataTransfer.files[0] ?? null;
      handleFileChange(dropped);
    },
    [handleFileChange],
  );

  const handlePublish = useCallback(async () => {
    if (sendingRef.current) return;
    const error = validateFile(file, t);
    if (error) {
      setValidationError(error);
      return;
    }
    sendingRef.current = true;
    setStep('sending');
    setBackendError(null);
    setMissingFiles([]);
    try {
      const res = await api.upload(file!);
      setResult(res);
      setStep('result');
    } catch (err) {
      sendingRef.current = false;

      if (ApiError.isApiError(err) && err.statusCode === 504) {
        setBackendError(t('upload.errors.timeout'));
        setStep('select');
        onSuccess();
        return;
      }

      if ((ApiError.isApiError(err) && err.isServerError) || !ApiError.isApiError(err)) {
        try {
          const fresh = await api.getCurrent();
          const freshVersion = fresh.general?.current?.version;
          if (freshVersion && freshVersion !== currentVersion) {
            const cur = fresh.general.current!;
            setResult({
              version: cur.version,
              hc_app_version: cur.hc_app_version,
              channel: 'general',
              uploaded_at: cur.uploaded_at,
              uploaded_by: cur.uploaded_by,
            });
            setStep('result');
            return;
          }
        } catch {
          // getCurrent failed too — fall through to generic error
        }
        setBackendError(t('upload.errors.serverError'));
        setStep('select');
        return;
      }

      if (ApiError.isApiError(err) && err.isForbidden) {
        setBackendError(t('upload.errors.forbidden'));
        setStep('select');
        return;
      }

      if (ApiError.isApiError(err)) {
        const reason = typeof err.details.reason === 'string' ? err.details.reason : undefined;
        if (reason === 'missing_files') {
          setBackendError(t('upload.errors.missing_files'));
          setMissingFiles(
            Array.isArray(err.details.missing_files)
              ? (err.details.missing_files as string[])
              : [],
          );
          setStep('select');
          return;
        }
        if (reason && KNOWN_REASONS.has(reason)) {
          setBackendError(t(`upload.errors.${reason}`));
          setStep('select');
          return;
        }
        setBackendError(err.messages[0] ?? t('upload.backendError'));
        setStep('select');
        return;
      }

      setBackendError(t('upload.backendError'));
      setStep('select');
    }
  }, [file, api, t, currentVersion, onSuccess]);

  const canPublish = !!file && !validationError && confirmed && step === 'select';

  return (
    <Dialog
      open={open}
      onClose={step === 'sending' ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={step === 'sending'}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {step === 'result' ? t('upload.result.title') : t('upload.title')}
        {step !== 'sending' && (
          <IconButton aria-label="close" onClick={handleClose} sx={{ ml: 'auto' }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
        {step === 'sending' && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography sx={{ color: theme.palette.text.subtle }}>{t('upload.sending')}</Typography>
          </Stack>
        )}

        {step === 'select' && (
          <Stack spacing={2}>
            {backendError && (
              <Alert severity="error">
                {backendError}
                {missingFiles.length > 0 && (
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {missingFiles.map(f => (
                      <li key={f}>
                        <Typography sx={{ fontSize: '0.85rem' }}>{f}</Typography>
                      </li>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {!file ? (
              <Box
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <CloudUploadOutlinedIcon
                  sx={{ fontSize: 40, color: theme.palette.text.subtle, mb: 1 }}
                />
                <Typography sx={{ color: theme.palette.text.subtle }}>
                  {t('upload.dropzone')}{' '}
                  <Typography
                    component="span"
                    sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
                  >
                    {t('upload.browse')}
                  </Typography>
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 1,
                }}
              >
                <InsertDriveFileOutlinedIcon sx={{ color: theme.palette.primary.main }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: theme.palette.text.body,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.subtle }}>
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => {
                    handleFileChange(null);
                    fileInputRef.current?.click();
                  }}
                >
                  {t('upload.replace')}
                </Button>
              </Box>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".7z"
              hidden
              onChange={e => {
                handleFileChange(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
            />

            {validationError && <Alert severity="error">{validationError}</Alert>}

            <FormControlLabel
              control={
                <Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
              }
              label={
                <Typography sx={{ fontSize: '0.85rem', color: theme.palette.text.body }}>
                  {t('upload.confirm')}
                </Typography>
              }
            />
          </Stack>
        )}

        {step === 'result' && result && (
          <Stack spacing={1.5}>
            <ResultRow label={t('upload.result.version')} value={result.version} />
            <ResultRow label={t('upload.result.hcAppVersion')} value={result.hc_app_version} />
            <ResultRow label={t('upload.result.channel')} value={t(`channel.${result.channel}`)} />
            <ResultRow
              label={t('upload.result.uploadedBy')}
              value={result.uploaded_by.username ?? '—'}
            />
            <ResultRow
              label={t('upload.result.uploadedAt')}
              value={formatDateTime(result.uploaded_at)}
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('upload.result.notice')}
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 'select' && (
          <>
            <Button onClick={handleClose}>{t('upload.cancel')}</Button>
            <Button variant="contained" onClick={handlePublish} disabled={!canPublish}>
              {t('upload.publish')}
            </Button>
          </>
        )}
        {step === 'result' && (
          <Button variant="contained" onClick={handleClose}>
            {t('upload.result.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ResultRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Typography
        sx={{
          width: 180,
          flexShrink: 0,
          fontSize: '0.85rem',
          color: theme.palette.text.label,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.85rem', color: theme.palette.text.body }}>{value}</Typography>
    </Box>
  );
}
